import React from 'react';
import type { AbsorbedChargesDto } from '../../lib/quotationApi';
import { profitPercents, type ProfitTtd, type QuotationTotals } from '../../utils/pricing';
import { round2 } from '../../utils/invoiceLineMath';
import { usdToTtd } from '../../utils/fxDisplay';
import { shownProfitPercents, type ShownProfitPercents } from './shownProfitPercents';
import { SummaryTtdCell } from './SummaryTtdCell';

// "Spread charges into prices" raises the line
// selling prices by the quotation's charges, leaving only the cents it cannot
// spread on a charge row (D170.5, server `utils/chargeSpread`). From then on the Pricing
// Summary's Profit includes that money — it is freight the client repays, not
// margin — and without these rows nothing on screen said how much of it there
// was. INTERNAL SCREEN ONLY: the exports, Client View and email never read
// `absorbedCharges`.

export interface TrueProfitFigures {
  /** The built-in charges in TT$, at the screen's pinned rate; null without one. */
  absorbedTtd: number | null;
  /** Shown US$ profit less the built-in charges. */
  usd: number;
  /** The TT$ profit figures with the built-in charges taken out; null when there is no TT$ profit. */
  ttd: ProfitTtd | null;
  /** The ONE percentage pair the screen states, picked the way the Profit row picks it. */
  percents: ShownProfitPercents;
}

/**
 * "True profit (excluding built-in charges)": the shown profit less
 * `absorbedTotal`, over the SAME cost and final selling price the Profit row
 * uses. Every percentage comes from `profitPercents` and the currency pick from
 * `shownProfitPercents`; the TT$ side subtracts the built-in charges' TT$ from
 * the composed TT$ profit (`resolveProfitTtd`), not from a converted US$ one.
 *
 * The selling-price base is the final selling price as it stands — i.e. WITH
 * the built-in charges inside it, since that is what the client pays.
 */
export function trueProfitFigures(
  totals: Pick<QuotationTotals, 'profit' | 'totalCost' | 'netSellingPrice'>,
  profitTtd: ProfitTtd | null,
  absorbedTotal: number,
  ttdToUsd: number | null
): TrueProfitFigures {
  const usd = round2(totals.profit - absorbedTotal);
  const absorbedTtd = usdToTtd(absorbedTotal, ttdToUsd);
  const ttdProfit = profitTtd && absorbedTtd != null ? round2(profitTtd.profit - absorbedTtd) : null;
  const ttd =
    profitTtd && ttdProfit != null
      ? { ...profitTtd, profit: ttdProfit, ...profitPercents(ttdProfit, profitTtd.costTtd, profitTtd.sellingTtd) }
      : null;
  const percents = shownProfitPercents(
    {
      ...profitPercents(usd, totals.totalCost, totals.netSellingPrice),
      cost: totals.totalCost,
      netSellingPrice: totals.netSellingPrice,
    },
    ttd
  );
  return { absorbedTtd, usd, ttd, percents };
}

const CELL_BORDER: React.CSSProperties = { padding: '4px 8px', borderWidth: '0 1px 1px 0', borderStyle: 'solid', borderColor: '#D7DBD9' };
const tone = (n: number) => (n >= 0 ? 'text-primary-500' : 'text-[#CC4B13]');
const pct = (percent: number, base: number) => (base > 0 ? `${percent.toFixed(1)}%` : '—');

const Row: React.FC<{ label: string; labelTitle: string; value: string; valueClass?: string; children: React.ReactNode }> = ({
  label,
  labelTitle,
  value,
  valueClass = 'text-slate-900',
  children,
}) => (
  <div className="group flex flex-row min-h-[28px]">
    <div className="w-6 flex-shrink-0 overflow-hidden" style={{ borderWidth: '0 0 1px 1px', borderStyle: 'solid', borderColor: '#D7DBD9' }} />
    <div className="flex justify-center items-center w-[126px] min-w-0 shrink overflow-hidden bg-[#F9FAF9]" style={CELL_BORDER}>
      <span className="text-slate-900 font-['Public_Sans'] font-medium text-xs text-center leading-tight" title={labelTitle}>
        {label}
      </span>
    </div>
    <div className={`flex justify-center items-center flex-1 min-w-0 overflow-hidden bg-white font-['Public_Sans'] text-xs ${valueClass}`} style={CELL_BORDER}>
      <span className="truncate" title={value}>
        {value}
      </span>
    </div>
    {children}
  </div>
);

/**
 * Three Pricing Summary rows, shown only when charges have been built into the
 * prices (`absorbed.total > 0`): the built-in amount, and the true profit
 * against cost price and against selling price. A quotation whose charges are
 * billed separately renders nothing here, exactly as before.
 */
export const AbsorbedChargesRows: React.FC<{
  absorbed: AbsorbedChargesDto | null | undefined;
  totals: Pick<QuotationTotals, 'profit' | 'totalCost' | 'netSellingPrice'>;
  profitTtd: ProfitTtd | null;
  ttdToUsd: number | null;
}> = ({ absorbed, totals, profitTtd, ttdToUsd }) => {
  const total = absorbed?.total || 0;
  if (!(total > 0)) return null;
  const f = trueProfitFigures(totals, profitTtd, total, ttdToUsd);
  const trueTitle = 'True profit (excluding built-in charges): the profit above less the charges built into the prices';
  return (
    <>
      <Row
        label="Charges built into prices"
        labelTitle="Custom expenses and other charges that 'Spread charges into prices' raised the line prices by. Internal only — the client sees the prices, not this row."
        value={`US$${total.toFixed(2)}`}
      >
        <SummaryTtdCell usd={total} ttd={f.absorbedTtd} ttdToUsd={ttdToUsd} />
      </Row>
      <Row
        label="True profit against cost price"
        labelTitle={`${trueTitle}, as a % of the cost price`}
        value={`US$${f.usd.toFixed(2)} (${pct(f.percents.ofCost, f.percents.costBase)})`}
        valueClass={tone(f.usd)}
      >
        <SummaryTtdCell
          usd={f.usd}
          ttd={f.ttd ? f.ttd.profit : null}
          percent={f.ttd?.profitPercentOfCost}
          ttdToUsd={ttdToUsd}
          className={tone(f.usd)}
          title="TT$ true profit ÷ TT$ cost"
        />
      </Row>
      <Row
        label="True profit against selling price"
        labelTitle={`${trueTitle}, as a % of the final selling price`}
        value={pct(f.percents.ofSelling, f.percents.sellingBase)}
        valueClass={`font-semibold ${tone(f.percents.ofSelling)}`}
      >
        <SummaryTtdCell
          usd={null}
          percent={f.ttd?.marginPercent}
          ttdToUsd={ttdToUsd}
          className={`font-semibold ${tone(f.ttd?.marginPercent ?? 0)}`}
          title="TT$ true profit ÷ TT$ final selling price"
        />
      </Row>
    </>
  );
};
